import * as React from 'react';
import Project from '../types/Project'
import styles from "./ProjectList.module.css";
import {List, ListHeader} from "./List";
import {
    ArrowSortDown16Regular,
    ArrowSortUp16Regular,
    FolderLink16Regular as FolderLink,
    MoreVertical20Regular as MoreVertical,
    Pin20Filled as PinFilled,
    Pin20Regular as Pin
} from "@fluentui/react-icons"
import {useOnClickOutside} from '@fluentui/react-utilities';
import {PopupMenu, PopupMenuHeader, PopupMenuItem} from "./PopupMenu";
import IconSort from "../icons/IconSort";
import {SortBy} from "../types/SortBy";
import Outlook from '../services/Outlook';

interface MenuItem {
    label: string,
    callback: () => void;
}

interface OptionsMenuProps {
    qualityUrl: string,
    synergyUrl?: string,
    projectId: string, 
    bulkFileFolder?: (projectId) => any | undefined, 
    menuItems: MenuItem[]
}

const OptionsMenu = ({ bulkFileFolder, projectId, qualityUrl, synergyUrl, menuItems }: OptionsMenuProps) => {
    const [open, setOpen] = React.useState(false);
    const [toastOpen, setToastOpen] = React.useState(false);
    const [toastMessage, setToastMessage] = React.useState('');
    const [toastTimeout, setToastTimeout] = React.useState<NodeJS.Timeout | undefined>();
    const ref = React.useRef(null);

    useOnClickOutside({ refs: [ref], element: document, callback: () => { setOpen(false) } });

    const toast = <div style={{padding: '4px 8px'}}>
        {toastMessage}
    </div>

    const showToast = (message: string) => {
        setToastMessage(message);
        setToastOpen(true);
        setToastTimeout(setTimeout(() => {
            setOpen(false);
            setToastOpen(false);
        }, 2000));
    }

    const openMenu = () => {
        clearTimeout(toastTimeout);
        setToastTimeout(undefined);
        setOpen(true);
        setToastOpen(false)
    }

    return (
        <div ref={ref} className={`${styles.toolButton} ${styles.flat}`} onClick={openMenu}>
            <MoreVertical className={styles.icon}></MoreVertical>
            <div className={styles.menu} style={{ display: open ? 'block' : 'none' }}>
                {toastOpen
                    ? toast
                    : <>
                        <div className={styles.header}>Useful links</div>
                        <div className={styles.menuItem} onClick={(e) => {
                            e.stopPropagation();
                            window.open(qualityUrl, '_blank'); 
                            setOpen(false); }}>
                            Go to Hub
                        </div>
                        { synergyUrl &&
                            <div className={styles.menuItem} onClick={(e) => {
                                e.stopPropagation();
                                window.open(synergyUrl, '_blank');
                                setOpen(false); }}>
                                Go to Synergy
                            </div>
                        }
                        {menuItems.length > 0 ?
                            <>
                                <hr />
                                <div className={styles.header}>Project Folders</div>
                                {menuItems.map((mi, index) => (
                                    <div key={index}
                                        className={styles.menuItem}
                                        onClick={(e) => { e.stopPropagation(); mi.callback(); showToast('Copied to clipboard'); }}>
                                        <FolderLink></FolderLink>
                                        <span>{mi.label}</span>
                                    </div>
                                ))}
                            </>
                            : <></>
                        }
                        { bulkFileFolder &&
                            <>
                                <div className={styles.header}>Bulk File</div>
                                <div className={styles.menuItem}
                                onClick={async (e) => {
                                    e.stopPropagation();
                                    bulkFileFolder(projectId);
                                    setOpen(false);
                                }}>
                                    File entire mail folder
                                </div>
                            </>
                        }
                    </>
                }
            </div>
        </div>
    )
} 

interface ProjectListProps {
    title: string,
    projects: Project[]
    onSelect?: (project: Project) => void;
    onPinClick?: (project: Project) => void;
    bulkFileFolder?: (projectId) => any, 
    listKey: string;
}

const ProjectList = ({title, projects: initialProjects, onSelect, onPinClick, bulkFileFolder, listKey}: ProjectListProps) => {

    React.useEffect(() => {
        setProjects(initialProjects);
    }, [initialProjects])

    const [projects, setProjects] = React.useState(initialProjects);

    const pinProject = (p: Project) => {
        p.pinned = true;
        setProjects(projects.slice());
        onPinClick(p);
    };

    const unpinProject = (p: Project) => {
        p.pinned = false;
        setProjects(projects.slice());
        onPinClick(p);
    }

    const createMenuItems = (p: Project): MenuItem[] => {
        const menuItems: MenuItem[] = [];

        if (!p.networkDriveInfos) return menuItems;

        const sortedNetworkDriveInfos = p
            .networkDriveInfos
            .sort((a, b) => a.name.localeCompare(b.name));

        for (const networkDriveInfo of sortedNetworkDriveInfos) {
            menuItems.push({
                label: networkDriveInfo.name,
                callback: async () => {

                    const input = document.createElement('input');
                    input.type = 'text';
                    input.value = networkDriveInfo.uri;
                    document.body.appendChild(input);
                    input.select();
                    document.execCommand('copy');
                    input.remove();

                    await navigator.clipboard.writeText(networkDriveInfo.uri);
                }
            });
        }

        return menuItems;
    }
    
    const jobNumberCompare = (a: string, b: string) => {
        const numA = parseFloat(a);
        const numB = parseFloat(b);
        const isNumA = !isNaN(numA);
        const isNumB = !isNaN(numB);
        if (isNumA && isNumB) 
            return numA - numB; 
        else if (isNumA)
            return -1; 
        else if (isNumB)
            return 1; 
        else
            return a.localeCompare(b); 
    }

    const keySortByPersist = `${listKey}ProjectListSortOrder`;
    const [sortBy, setSortBy] = React.useState(() => Outlook.getFromLocalStorage<SortBy>(keySortByPersist) ?? SortBy.ByLastRecentlyFiledTo);
    Outlook.setInLocalStorage(keySortByPersist, sortBy);

    const keyReverseSortPersist = `${listKey}ProjectListSortOrderReverse`;
    const [reverseSort, setReverseSort] = React.useState(() => Outlook.getFromLocalStorage<boolean>(keyReverseSortPersist) ?? false);
    Outlook.setInLocalStorage(keyReverseSortPersist, reverseSort);
    
    let projectsSorted = [...projects];
    if (sortBy === SortBy.ByJobName)
        projectsSorted = projectsSorted.sort((a, b) => a.name.localeCompare(b.name));
    else if (sortBy === SortBy.ByJobNumber)
        projectsSorted = projectsSorted.sort((a, b) => jobNumberCompare(b.number, a.number));
    if (reverseSort)
        projectsSorted.reverse();

    const jobListItems = projectsSorted.map(p => (
    <li key={`${p.id}`}>
            <div className={styles.jobRowItem}>
                <div style={{gap: '4px', flex: 1}}>
                    <div className={styles.jobLabel} title={`File email to ${p.name}`} onClick={() => onSelect(p)}>
                        <span className={styles.jobNumber}>{p.number}</span>
                        <span className={styles.jobName}>{p.name}</span>
                    </div>
                </div>
                <div style={{gap: 0}}>
                    <div className={`${styles.toolButton} ${styles.pinToolButton}`}>
                        {(unpinProject && pinProject) && p.pinned
                            ? <PinFilled className={`${styles.icon}`} onClick={() => { unpinProject(p); }}></PinFilled>
                            : <Pin className={`${styles.icon}`} onClick={() => { pinProject(p); }}></Pin>
                        }
                    </div>
                    <OptionsMenu projectId={p.id}
                                 bulkFileFolder={bulkFileFolder}        
                                 synergyUrl={p.synergyUri} qualityUrl={p.qualityUri} 
                                 menuItems={createMenuItems(p)}/>
                </div>
            </div>

        </li>)
    );
    
    const noJobsLabel = <li key={'none'}>
        <div>
            None available.
        </div>
    </li>

    const [menuIsOpen, setMenuIsOpen] = React.useState(false);
    const toggleMenuOpen = () => setMenuIsOpen(x => !x);
    const sortIsDesc = reverseSort !== (sortBy !== SortBy.ByJobNumber);  
    
    const clickSortBy = (s: SortBy) =>
    {
        if (sortBy === s)
            setReverseSort(x => !x);
        else{
            setSortBy(s);
            setReverseSort(false);
        }
        setMenuIsOpen(false);
    }
    
    const sortMenuItem = (menuItemSortBy: SortBy)=> {
        let title = "Last recently filed to";
        if (menuItemSortBy === SortBy.ByJobNumber)
            title = "Job Number"
        else if (menuItemSortBy === SortBy.ByJobName)
            title = "Job Name"
        return <>
            <PopupMenuItem onClick={() => clickSortBy(menuItemSortBy)}>
                <div className={styles.sortMenuItem}>
                    <span> {title} </span>
                    {sortBy === menuItemSortBy && (sortIsDesc ? <ArrowSortDown16Regular/>:<ArrowSortUp16Regular/>)}    
                </div>
            </PopupMenuItem>
        </>
    }
    
    return (
        <List className={styles.list}>
            <ListHeader title={title}>
                <div className={styles.headerButton}>
                    { projects.length > 1 &&
                        <IconSort desc={sortIsDesc}  className={styles.sortIcon}
                                  onClick={toggleMenuOpen} title="Sort projects"/>
                    }
                    <PopupMenu isOpen={menuIsOpen} onClickOutside={() => setMenuIsOpen(false)}>
                        <PopupMenuHeader>Sort by</PopupMenuHeader>
                        {sortMenuItem(SortBy.ByJobNumber)}
                        {sortMenuItem(SortBy.ByJobName)}
                        {sortMenuItem(SortBy.ByLastRecentlyFiledTo)}
                    </PopupMenu>
                </div>    
            </ListHeader>
            {initialProjects.length > 0 ? jobListItems : noJobsLabel}
        </List>);
}

export default ProjectList;